import { useState, useEffect, useMemo, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import CatalogPage from "@/pages/CatalogPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProducts from "@/pages/AdminProducts";
import AdminCategories from "@/pages/AdminCategories";
import AdminUsers from "@/pages/AdminUsers";
import AdminImport from "@/pages/AdminImport";
import AdminPricing from "@/pages/AdminPricing";
import AdminInvites from "@/pages/AdminInvites";
import AdminBackup from "@/pages/AdminBackup";
import AdminImports from "@/pages/AdminImports";
import AdminPhotos from "@/pages/AdminPhotos";
import AdminExports from "@/pages/AdminExports";
import AgentDashboard from "@/pages/AgentDashboard";
import AgentClients from "@/pages/AgentClients";
import AdminClients from "@/pages/AdminClients";
import AdminEstrattoConto from "@/pages/AdminEstrattoConto";
import AdminRules from "@/pages/AdminRules";
import AdminSalesStats from "@/pages/AdminSalesStats";
import QuotesPage from "@/pages/QuotesPage";
import QuoteEditor from "@/pages/QuoteEditor";
import WarehouseDashboard from "@/pages/WarehouseDashboard";
import OrdersPage from "@/pages/OrdersPage";
import OrderEditor from "@/pages/OrderEditor";
import GiriOrdineRapido from "@/pages/GiriOrdineRapido";
import VoiceOrdersPage from "@/pages/VoiceOrdersPage";
import AdminClientiFuoriGiri from "@/pages/AdminClientiFuoriGiri";
import OrderDetailView from "@/pages/OrderDetailView";
import PickingListPage from "@/pages/PickingListPage";
import DeliveryRoundsPage from "@/pages/DeliveryRoundsPage";
import DeliveryRoundDetail from "@/pages/DeliveryRoundDetail";
import AdminClientPrices from "@/pages/AdminClientPrices";
import AdminPromotions from "@/pages/AdminPromotions";
import ClientCart from "@/pages/ClientCart";
import AdminPushStatus from "@/pages/AdminPushStatus";
import AdminDropboxSync from "@/pages/AdminDropboxSync";
import AdminGiacenze from "@/pages/AdminGiacenze";
import AdminAgentsAccess from "@/pages/AdminAgentsAccess";
import AdminActiveSessions from "@/pages/AdminActiveSessions";
import AdminClientAnalytics from "@/pages/AdminClientAnalytics";
import AdminForecast from "@/pages/AdminForecast";
import AdminGiriMap from "@/pages/AdminGiriMap";
import HaccpDashboard from "@/pages/haccp/HaccpDashboard";
import HaccpTemperatures from "@/pages/haccp/HaccpTemperatures";
import HaccpCleaning from "@/pages/haccp/HaccpCleaning";
import HaccpNonConformities from "@/pages/haccp/HaccpNonConformities";
import HaccpDocuments from "@/pages/haccp/HaccpDocuments";
import HaccpSettings from "@/pages/haccp/HaccpSettings";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import GeoPermissionBanner from "@/components/GeoPermissionBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { installAutoSync } from "@/utils/pendingOrderSync";

// Prefer same-origin in the browser (avoids cross-origin 3rd-party cookie blocks
// when the app is served on a custom domain like triolo.store but built with a
// different REACT_APP_BACKEND_URL). Falls back to the env var on the server / SSR.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL =
  typeof window !== "undefined" &&
  window.location &&
  window.location.protocol === "https:"
    ? window.location.origin
    : ENV_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Axios instance sempre con withCredentials per inviare il cookie httpOnly
  const authAxios = useMemo(() => {
    const inst = axios.create({ baseURL: API, withCredentials: true });
    return inst;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Con withCredentials il browser invia automaticamente il cookie httpOnly
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  // Once authenticated, install the offline order sync. It listens for the
  // 'online' event and drains the IndexedDB pending_orders queue automatically.
  useEffect(() => {
    if (!user) return undefined;
    const teardown = installAutoSync(authAxios);
    const onFlushed = (e) => {
      const sent = e?.detail?.sent || 0;
      if (sent > 0) {
        toast.success(`Sincronizzati ${sent} ordini offline`);
      }
    };
    window.addEventListener("pendingOrdersFlushed", onFlushed);
    return () => {
      window.removeEventListener("pendingOrdersFlushed", onFlushed);
      if (typeof teardown === "function") teardown();
    };
  }, [user, authAxios]);

  const login = useCallback(async (email, password) => {
    // Il backend imposta il cookie httpOnly automaticamente. withCredentials è obbligatorio.
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { user: userData } = response.data;
    setUser(userData);
    toast.success(`Benvenuto, ${userData.name}!`);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* non bloccante */ }
    setUser(null);
    toast.info("Logout effettuato");
  }, []);

  // Modalità Demo "Vedi come" — admin imposta un cookie per agire come un altro utente.
  const impersonate = useCallback(async (targetUserId) => {
    const r = await axios.post(
      `${API}/admin/impersonate/${targetUserId}`,
      {},
      { withCredentials: true }
    );
    // Ricarica l'utente corrente: ora il backend restituirà l'utente impersonato
    const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(me.data);
    return r.data;
  }, []);

  const stopImpersonate = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/impersonate/stop`, {}, { withCredentials: true });
    } catch { /* non bloccante */ }
    const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(me.data);
  }, []);

  const ctxValue = useMemo(
    () => ({ user, loading, login, logout, authAxios, impersonate, stopImpersonate }),
    [user, loading, login, logout, authAxios, impersonate, stopImpersonate]
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Agent con poteri magazziniere ha accesso anche alle rotte /warehouse/*
    const isAgentExtra = user.role === "agent" && user.magazziniere_extra && allowedRoles.includes("magazziniere");
    if (!isAgentExtra) {
      if (user.role === "agent") return <Navigate to="/agent/catalog" replace />;
      if (user.role === "magazziniere") return <Navigate to="/warehouse" replace />;
      return <Navigate to="/catalog" replace />;
    }
  }

  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === "admin" ? "/admin" : user.role === "agent" ? "/agent/catalog" : user.role === "magazziniere" ? "/warehouse" : "/catalog"} replace /> : <LoginPage />} />
      <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute allowedRoles={["admin"]}><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCategories /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute allowedRoles={["admin"]}><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/estratto-conto" element={<ProtectedRoute allowedRoles={["admin"]}><AdminEstrattoConto /></ProtectedRoute>} />
      <Route path="/admin/rules" element={<ProtectedRoute allowedRoles={["admin"]}><AdminRules /></ProtectedRoute>} />
      <Route path="/admin/sales-stats" element={<ProtectedRoute allowedRoles={["admin"]}><AdminSalesStats /></ProtectedRoute>} />
      <Route path="/admin/quotes" element={<ProtectedRoute allowedRoles={["admin"]}><QuotesPage /></ProtectedRoute>} />
      <Route path="/admin/quotes/new" element={<ProtectedRoute allowedRoles={["admin"]}><QuoteEditor /></ProtectedRoute>} />
      <Route path="/admin/quotes/:quoteId/edit" element={<ProtectedRoute allowedRoles={["admin"]}><QuoteEditor /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["admin"]}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/agents-access" element={<ProtectedRoute allowedRoles={["admin"]}><AdminAgentsAccess /></ProtectedRoute>} />
      <Route path="/admin/active-sessions" element={<ProtectedRoute allowedRoles={["admin"]}><AdminActiveSessions /></ProtectedRoute>} />
      <Route path="/admin/client-analytics" element={<ProtectedRoute allowedRoles={["admin"]}><AdminClientAnalytics /></ProtectedRoute>} />
      <Route path="/admin/forecast" element={<ProtectedRoute allowedRoles={["admin"]}><AdminForecast /></ProtectedRoute>} />
      <Route path="/admin/giri-mappa" element={<ProtectedRoute allowedRoles={["admin", "agent"]}><AdminGiriMap /></ProtectedRoute>} />
      <Route path="/admin/haccp" element={<ProtectedRoute allowedRoles={["admin"]}><HaccpDashboard /></ProtectedRoute>} />
      <Route path="/admin/haccp/temperature" element={<ProtectedRoute allowedRoles={["admin"]}><HaccpTemperatures /></ProtectedRoute>} />
      <Route path="/admin/haccp/pulizie" element={<ProtectedRoute allowedRoles={["admin"]}><HaccpCleaning /></ProtectedRoute>} />
      <Route path="/admin/haccp/non-conformita" element={<ProtectedRoute allowedRoles={["admin"]}><HaccpNonConformities /></ProtectedRoute>} />
      <Route path="/admin/haccp/schede" element={<ProtectedRoute allowedRoles={["admin"]}><HaccpDocuments /></ProtectedRoute>} />
      <Route path="/admin/haccp/impostazioni" element={<ProtectedRoute allowedRoles={["admin"]}><HaccpSettings /></ProtectedRoute>} />
      <Route path="/admin/import" element={<ProtectedRoute allowedRoles={["admin"]}><AdminImport /></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPricing /></ProtectedRoute>} />
      <Route path="/admin/client-prices" element={<ProtectedRoute allowedRoles={["admin"]}><AdminClientPrices /></ProtectedRoute>} />
      <Route path="/admin/promotions" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPromotions /></ProtectedRoute>} />
      <Route path="/admin/invites" element={<ProtectedRoute allowedRoles={["admin"]}><AdminInvites /></ProtectedRoute>} />
      <Route path="/admin/backup" element={<ProtectedRoute allowedRoles={["admin"]}><AdminBackup /></ProtectedRoute>} />
      <Route path="/admin/imports" element={<ProtectedRoute allowedRoles={["admin"]}><AdminImports /></ProtectedRoute>} />
      <Route path="/admin/photos" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPhotos /></ProtectedRoute>} />
      <Route path="/admin/exports" element={<ProtectedRoute allowedRoles={["admin", "agent"]}><AdminExports /></ProtectedRoute>} />
      <Route path="/agent" element={<ProtectedRoute allowedRoles={["agent"]}><AgentDashboard /></ProtectedRoute>} />
      <Route path="/agent/catalog" element={<ProtectedRoute allowedRoles={["agent"]}><CatalogPage /></ProtectedRoute>} />
      <Route path="/agent/catalog/:categoryId" element={<ProtectedRoute allowedRoles={["agent"]}><CatalogPage /></ProtectedRoute>} />
      <Route path="/agent/clients" element={<ProtectedRoute allowedRoles={["agent", "admin"]}><AgentClients /></ProtectedRoute>} />
      <Route path="/agent/quotes" element={<ProtectedRoute allowedRoles={["agent"]}><QuotesPage /></ProtectedRoute>} />
      <Route path="/agent/quotes/new" element={<ProtectedRoute allowedRoles={["agent"]}><QuoteEditor /></ProtectedRoute>} />
      <Route path="/agent/quotes/:quoteId/edit" element={<ProtectedRoute allowedRoles={["agent"]}><QuoteEditor /></ProtectedRoute>} />
      {/* Order routes - Admin */}
      <Route path="/admin/orders" element={<ProtectedRoute allowedRoles={["admin"]}><OrdersPage /></ProtectedRoute>} />
      <Route path="/admin/orders/new" element={<ProtectedRoute allowedRoles={["admin"]}><OrderEditor /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId/edit" element={<ProtectedRoute allowedRoles={["admin"]}><OrderEditor /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId" element={<ProtectedRoute allowedRoles={["admin"]}><OrderDetailView /></ProtectedRoute>} />
      <Route path="/admin/delivery-rounds" element={<ProtectedRoute allowedRoles={["admin"]}><DeliveryRoundsPage /></ProtectedRoute>} />
      <Route path="/admin/delivery-rounds/:roundId" element={<ProtectedRoute allowedRoles={["admin"]}><DeliveryRoundDetail /></ProtectedRoute>} />
      <Route path="/admin/push-status" element={<ProtectedRoute allowedRoles={["admin", "agent"]}><AdminPushStatus /></ProtectedRoute>} />
      <Route path="/admin/dropbox-sync" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDropboxSync /></ProtectedRoute>} />
      <Route path="/admin/giacenze" element={<ProtectedRoute allowedRoles={["admin"]}><AdminGiacenze /></ProtectedRoute>} />
      {/* Order routes - Agent */}
      <Route path="/agent/orders" element={<ProtectedRoute allowedRoles={["agent"]}><OrdersPage /></ProtectedRoute>} />
      <Route path="/agent/orders/new" element={<ProtectedRoute allowedRoles={["agent"]}><OrderEditor /></ProtectedRoute>} />
      <Route path="/agent/orders/:orderId/edit" element={<ProtectedRoute allowedRoles={["agent"]}><OrderEditor /></ProtectedRoute>} />
      <Route path="/agent/orders/:orderId" element={<ProtectedRoute allowedRoles={["agent"]}><OrderDetailView /></ProtectedRoute>} />
      {/* Giri Presa Ordine */}
      <Route path="/admin/giri" element={<ProtectedRoute allowedRoles={["admin"]}><GiriOrdineRapido /></ProtectedRoute>} />
      <Route path="/admin/voice-orders" element={<ProtectedRoute allowedRoles={["admin"]}><VoiceOrdersPage /></ProtectedRoute>} />
      <Route path="/admin/clienti-fuori-giri" element={<ProtectedRoute allowedRoles={["admin"]}><AdminClientiFuoriGiri /></ProtectedRoute>} />
      <Route path="/agent/giri" element={<ProtectedRoute allowedRoles={["agent"]}><GiriOrdineRapido /></ProtectedRoute>} />
      <Route path="/warehouse/giri" element={<ProtectedRoute allowedRoles={["magazziniere"]}><GiriOrdineRapido /></ProtectedRoute>} />
      {/* Warehouse routes (also accessible to agents with magazziniere_extra flag) */}
      <Route path="/warehouse" element={<ProtectedRoute allowedRoles={["magazziniere"]}><WarehouseDashboard /></ProtectedRoute>} />
      <Route path="/warehouse/orders" element={<ProtectedRoute allowedRoles={["magazziniere"]}><OrdersPage /></ProtectedRoute>} />
      <Route path="/warehouse/orders/new" element={<ProtectedRoute allowedRoles={["magazziniere"]}><OrderEditor /></ProtectedRoute>} />
      <Route path="/warehouse/orders/:orderId/edit" element={<ProtectedRoute allowedRoles={["magazziniere"]}><OrderEditor /></ProtectedRoute>} />
      <Route path="/warehouse/orders/:orderId" element={<ProtectedRoute allowedRoles={["magazziniere"]}><OrderDetailView /></ProtectedRoute>} />
      <Route path="/warehouse/picking" element={<ProtectedRoute allowedRoles={["magazziniere"]}><PickingListPage /></ProtectedRoute>} />
      <Route path="/warehouse/delivery-rounds" element={<ProtectedRoute allowedRoles={["magazziniere"]}><DeliveryRoundsPage /></ProtectedRoute>} />
      <Route path="/warehouse/delivery-rounds/:roundId" element={<ProtectedRoute allowedRoles={["magazziniere"]}><DeliveryRoundDetail /></ProtectedRoute>} />
      {/* Client routes - visibility on own orders */}
      <Route path="/client/orders" element={<ProtectedRoute allowedRoles={["client"]}><OrdersPage /></ProtectedRoute>} />
      <Route path="/client/orders/:orderId" element={<ProtectedRoute allowedRoles={["client"]}><OrderDetailView /></ProtectedRoute>} />
      <Route path="/client/cart" element={<ProtectedRoute allowedRoles={["client"]}><ClientCart /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/catalog" replace />} />
      <Route path="*" element={<Navigate to="/catalog" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App">
            <ImpersonationBanner />
            <AppRoutes />
            <PushPermissionBanner />
            <GeoPermissionBanner />
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
